"use client";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "use-intl";

import { ReleaseImageLightboxView } from "@/components/release-image-lightbox-view";
import type { ReleaseImage } from "@garimpa/api/music/getReleaseImages";
import {
  buildReleaseGalleryImageSrcSet,
  buildReleaseGalleryImageUrl,
} from "@garimpa/lib/imageUrls";

interface ReleaseImageLightboxProps {
  analyticsSurface?: string;
  images: ReleaseImage[];
  imageCount: number;
  initialIndex: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  releaseId: string;
  title: string;
}

const DISPLAY_IMAGE_WIDTHS = [640, 960, 1280, 1600] as const;
const DISPLAY_IMAGE_SIZES = "90vw";
const activeImagePrefetches = new Map<string, HTMLImageElement>();

function prefetchDisplayImage(releaseId: string, imageIndex: number) {
  const src = buildReleaseGalleryImageUrl(releaseId, imageIndex);
  if (activeImagePrefetches.has(src)) return;

  const image = new Image();
  const clear = () => activeImagePrefetches.delete(src);
  activeImagePrefetches.set(src, image);
  image.onload = clear;
  image.onerror = clear;
  image.decoding = "async";
  image.sizes = DISPLAY_IMAGE_SIZES;
  const srcSet = buildReleaseGalleryImageSrcSet(releaseId, imageIndex, DISPLAY_IMAGE_WIDTHS);
  if (srcSet) image.srcset = srcSet;
  image.src = src;
}

export function ReleaseImageLightbox({
  analyticsSurface,
  images,
  imageCount,
  initialIndex,
  onClose,
  onIndexChange,
  releaseId,
  title,
}: ReleaseImageLightboxProps) {
  const t = useTranslations("release");
  const [selectedIndex, setSelectedIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(imageCount - 1, 0)),
  );
  const [assetLoading, setAssetLoading] = useState(true);
  const totalImages = images.length || imageCount;
  const selectedImage = images[selectedIndex];
  const selectedDisplayUrl = buildReleaseGalleryImageUrl(releaseId, selectedIndex);
  const selectedDisplaySrcSet = buildReleaseGalleryImageSrcSet(
    releaseId,
    selectedIndex,
    DISPLAY_IMAGE_WIDTHS,
  );
  const selectedAssetUrl =
    selectedImage?.assetType === "pdf"
      ? (selectedImage.originalUri ?? selectedDisplayUrl)
      : selectedDisplayUrl;
  const selectedAssetType = selectedImage?.assetType ?? "image";

  const goNext = useCallback(() => {
    setSelectedIndex((index) => (index + 1) % totalImages);
  }, [totalImages]);

  const goPrevious = useCallback(() => {
    setSelectedIndex((index) => (index - 1 + totalImages) % totalImages);
  }, [totalImages]);

  useEffect(() => {
    for (let imageIndex = 0; imageIndex < totalImages; imageIndex += 1) {
      prefetchDisplayImage(releaseId, imageIndex);
    }
  }, [releaseId, totalImages]);

  useEffect(() => {
    onIndexChange?.(selectedIndex);
  }, [onIndexChange, selectedIndex]);

  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(totalImages - 1, 0)));
  }, [totalImages]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowRight") goNext();
      else if (event.key === "ArrowLeft") goPrevious();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrevious, onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    setAssetLoading(true);
  }, [selectedAssetType, selectedAssetUrl]);

  const selectedImageLabel = t("photoOf", {
    number: selectedIndex + 1,
    total: totalImages,
  });

  return createPortal(
    <ReleaseImageLightboxView
      analyticsSurface={analyticsSurface}
      assetLoading={assetLoading}
      assetType={selectedAssetType}
      assetUrl={selectedAssetUrl}
      displaySrcSet={selectedDisplaySrcSet}
      displayUrl={selectedDisplayUrl}
      imageLabel={selectedImageLabel}
      onClose={onClose}
      onLoadEnd={() => setAssetLoading(false)}
      onNext={goNext}
      onPrevious={goPrevious}
      sizes={DISPLAY_IMAGE_SIZES}
      title={title}
      totalImages={totalImages}
    />,
    document.body,
  );
}
